import {
  Button,
  ButtonGroup,
  Input,
  ModalProps,
  Spacer,
  TextBody,
  TextTitle,
} from '@pypestream/design-system';
import { FC, RefObject, useEffect, useState } from 'react';

import { GenericModal } from '../generic-modal';

export interface EditChannelNameModalProps {
  modalRef: RefObject<ModalProps>;
  /** The name currently held by the channel form. */
  currentName: string;
  /** Hands the trimmed new name back; the caller owns persistence. */
  onSave: (name: string) => void;
  onClose: () => void;
}

/**
 * Rename a channel, reached from the kebab in the Channel Settings header
 * (PSTUDIO-3768). The Channel Details card that used to hold this field is
 * create-only now.
 *
 * This does NOT persist on its own. It writes the name back into the channel
 * form, which raises the shared "unsaved changes" bar, and the page's single
 * Save Changes writes it with everything else. That matters: the widget PUT
 * sends `style`, `beta` and `custom_pype_css` on every call, so a name-only
 * mutation fired from here would blank the channel's branding.
 */
export const EditChannelNameModal: FC<EditChannelNameModalProps> = ({
  modalRef,
  currentName,
  onSave,
  onClose,
}) => {
  const [draft, setDraft] = useState(currentName);
  const [touched, setTouched] = useState(false);

  // The modal instance outlives any one opening, so re-seed the draft whenever
  // the form's name changes underneath it (including after a cancel).
  useEffect(() => {
    setDraft(currentName);
    setTouched(false);
  }, [currentName]);

  const trimmed = draft.trim();
  const hasError = touched && !trimmed;

  const handleClose = () => {
    setDraft(currentName);
    setTouched(false);
    onClose();
  };

  const handleSave = () => {
    setTouched(true);

    if (!trimmed) {
      return;
    }

    onSave(trimmed);
    onClose();
  };

  return (
    <GenericModal
      test-id="ps-edit-channel-name-modal"
      modalRef={modalRef}
      onClose={handleClose}
    >
      <TextTitle size="small">Rename Channel</TextTitle>
      <Spacer size="large" />
      <TextBody size="small">Channel Name</TextBody>
      <Spacer size="2xsmall" />
      <Input
        test-id="ps-edit-channel-name-modal-input"
        variant="outlined"
        name="channel-name"
        value={draft}
        hasError={hasError}
        helpText={hasError ? 'Channel name is required' : ''}
        // @ts-expect-error - DS Input forwards onInput with target.value
        onInput={(e: { target: HTMLInputElement }) => {
          setTouched(true);
          setDraft(e.target.value);
        }}
      />
      <ButtonGroup slot="footer">
        <Button
          test-id="ps-edit-channel-name-modal-cancel"
          width="full"
          variant="secondary"
          size="large"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          test-id="ps-edit-channel-name-modal-save"
          width="full"
          size="large"
          disabled={!trimmed}
          onClick={handleSave}
        >
          Save
        </Button>
      </ButtonGroup>
    </GenericModal>
  );
};
